import type { ClientPositionView } from "@solomon-apps/shared";
import { BigDecimal } from "@solomon-labs/bigdecimal";

/** Annualized simple return from completed settlement periods, in percent. */
export function positionRewardRate(
    holdings: readonly (readonly ClientPositionView[])[],
    mode: "paid" | "current"
): BigDecimal | null {
    if (holdings.some((positions) => positions.some((position) => !position.rewardMetrics))) return null;
    let earned = new BigDecimal(0);
    let exposure = new BigDecimal(0);
    const latestEnd = Math.max(
        ...holdings.flatMap((positions) =>
            positions.flatMap((position) => position.rewardMetrics!.periods.map((period) => Date.parse(period.endsAt)))
        )
    );
    for (const positions of holdings) {
        const periods = positions
            .flatMap((position, index) =>
                position.rewardMetrics!.periods.map((period) => ({
                    ...period,
                    position: index,
                    start: Date.parse(period.startsAt),
                    end: Date.parse(period.endsAt)
                }))
            )
            .filter((period) => mode !== "current" || period.end === latestEnd);
        if (
            periods.some(
                (period) => !Number.isFinite(period.start) || !Number.isFinite(period.end) || period.start >= period.end
            )
        )
            return null;
        const boundaries = [...new Set(periods.flatMap((period) => [period.start, period.end]))].sort((a, b) => a - b);
        for (let i = 1; i < boundaries.length; i++) {
            const start = boundaries[i - 1];
            const end = boundaries[i];
            const active = periods.filter((period) => period.start <= start && period.end >= end);
            // Overlapping results for one position do not prove distinct earnings.
            if (new Set(active.map((period) => period.position)).size !== active.length) return null;
            let balance = new BigDecimal(0);
            for (const period of active) {
                const amount = new BigDecimal(period.averageBalance);
                if (mode === "paid" && period.paidRewards === null) return null;
                const reward = new BigDecimal(mode === "paid" ? period.paidRewards! : period.calculatedRewards);
                if (amount.isNaN() || reward.isNaN() || amount.lt(0) || reward.lt(0)) return null;
                if (reward.gt(0) && !amount.gt(0)) return null;
                if (amount.gt(balance)) balance = amount;
                earned = earned.add(reward.multiply(end - start).divide(period.end - period.start));
            }
            // One physical holding can participate in several programs.
            exposure = exposure.add(balance.multiply(end - start));
        }
    }
    return exposure.gt(0)
        ? earned
              .multiply(365 * 86_400_000)
              .multiply(100)
              .divide(exposure)
        : null;
}
